import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Eye, User, Search, CreditCard, TrendingUp, Target, 
  MessageCircle, Palette, ChevronDown, ChevronUp, 
  Sparkles, Loader2, BookOpen, Heart, Star, Quote
} from 'lucide-react';

const iconMap = {
  'eye': Eye,
  'user': User,
  'search': Search,
  'id-card': CreditCard,
  'trending-up': TrendingUp,
  'target': Target,
  'message-circle': MessageCircle,
  'palette': Palette
};

const RevelationSection = ({ section, isExpanded, onToggle }) => {
  const IconComponent = iconMap[section.icon] || Sparkles;
  
  const renderData = (data) => {
    if (!data) return null;
    
    // Helper to render object properties nicely
    const renderObjectNicely = (obj) => {
      if (typeof obj === 'string') {
        // Try to parse if it looks like JSON
        try {
          const parsed = JSON.parse(obj);
          if (typeof parsed === 'object') {
            return renderObjectNicely(parsed);
          }
        } catch (e) {
          // Not JSON, return as string
          return <p className="text-gray-300 text-sm whitespace-pre-wrap leading-relaxed">{obj}</p>;
        }
        return <p className="text-gray-300 text-sm whitespace-pre-wrap leading-relaxed">{obj}</p>;
      }
      
      if (Array.isArray(obj)) {
        return (
          <ul className="space-y-2">
            {obj.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                <Star className="w-3 h-3 text-[#D4AF37] mt-1 flex-shrink-0" />
                <span>{typeof item === 'string' ? item : JSON.stringify(item)}</span>
              </li>
            ))}
          </ul>
        );
      }
      
      if (typeof obj === 'object' && obj !== null) {
        return (
          <div className="space-y-3">
            {Object.entries(obj).map(([k, v]) => {
              if (!v || k === '_id') return null;
              const label = k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              return (
                <div key={k} className="border-l-2 border-[#D4AF37]/30 pl-4">
                  <span className="text-[#D4AF37] text-xs font-medium uppercase tracking-wider">{label}</span>
                  <p className="text-gray-300 text-sm mt-1 leading-relaxed">{String(v)}</p>
                </div>
              );
            })}
          </div>
        );
      }
      
      return <p className="text-gray-300 text-sm">{String(obj)}</p>;
    };
    
    // Special handling for reviews
    if (section.id === 'reflections' && data.reviews) {
      return (
        <div className="space-y-4">
          {data.reviews.map((review, idx) => (
            <div key={idx} className="bg-[#0A0A0A] border border-[#1C1C1C] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[#D4AF37] text-sm font-medium">{review.step}</span>
                {review.feeling && (
                  <span className="px-2 py-0.5 bg-[#D4AF37]/10 text-[#E5C585] text-xs rounded-full">
                    {review.feeling}
                  </span>
                )}
              </div>
              {review.insight && (
                <p className="text-gray-300 text-sm mb-2">{review.insight}</p>
              )}
              {review.next_action && (
                <p className="text-gray-500 text-xs italic">→ {review.next_action}</p>
              )}
            </div>
          ))}
        </div>
      );
    }
    
    // Handle artistic direction data
    if (section.id === 'artistic' && typeof data === 'object') {
      return (
        <div className="space-y-4">
          {Object.entries(data).map(([key, value]) => {
            if (!value || key === '_id') return null;
            const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            return (
              <div key={key} className="bg-[#0A0A0A] border border-[#1C1C1C] rounded-xl p-4">
                <h4 className="text-[#E5C585] text-sm font-medium mb-2">{formattedKey}</h4>
                <p className="text-gray-300 text-sm whitespace-pre-wrap">{String(value)}</p>
              </div>
            );
          })}
        </div>
      );
    }
    
    // Default rendering for other sections
    return (
      <div className="grid gap-4">
        {Object.entries(data).map(([key, value]) => {
          if (!value || key === '_id') return null;
          
          const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          
          // Handle arrays
          if (Array.isArray(value)) {
            return (
              <div key={key} className="bg-[#0A0A0A] border border-[#1C1C1C] rounded-xl p-4">
                <h4 className="text-[#E5C585] text-sm font-medium mb-3">{formattedKey}</h4>
                <ul className="space-y-2">
                  {value.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                      <Star className="w-3 h-3 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span>{typeof item === 'string' ? item : JSON.stringify(item)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }
          
          // Handle objects - render them nicely
          if (typeof value === 'object') {
            return (
              <div key={key} className="bg-[#0A0A0A] border border-[#1C1C1C] rounded-xl p-4">
                <h4 className="text-[#E5C585] text-sm font-medium mb-3">{formattedKey}</h4>
                {renderObjectNicely(value)}
              </div>
            );
          }
          
          // Handle strings that might be JSON
          if (typeof value === 'string') {
            return (
              <div key={key} className="bg-[#0A0A0A] border border-[#1C1C1C] rounded-xl p-4">
                <h4 className="text-[#E5C585] text-sm font-medium mb-3">{formattedKey}</h4>
                {renderObjectNicely(value)}
              </div>
            );
          }
          
          // Handle numbers and other types
          return (
            <div key={key} className="bg-[#0A0A0A] border border-[#1C1C1C] rounded-xl p-4">
              <h4 className="text-[#E5C585] text-sm font-medium mb-2">{formattedKey}</h4>
              <p className="text-gray-300 text-sm whitespace-pre-wrap">{String(value)}</p>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-[#1C1C1C] rounded-2xl overflow-hidden"
    >
      {/* Header - Always visible */}
      <button
        onClick={onToggle}
        className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
            <IconComponent className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div className="text-left">
            <h3 className="font-serif text-lg text-white">{section.title}</h3>
            <p className="text-gray-500 text-sm">{section.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {section.created_at && (
            <span className="text-gray-600 text-xs hidden sm:block">
              {new Date(section.created_at).toLocaleDateString('fr-FR')}
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-[#D4AF37]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </div>
      </button>
      
      {/* Content - Expandable */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 pt-2 border-t border-[#1C1C1C]">
              {renderData(section.data)}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const MyRevelations = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [revelations, setRevelations] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRevelations = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/my-revelations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRevelations(response.data);
        
        // Auto-expand first section if exists
        if (response.data.sections?.length > 0) {
          setExpandedSections(new Set([response.data.sections[0].id]));
        }
      } catch (err) {
        console.error('Error fetching revelations:', err);
        setError('Impossible de charger vos révélations');
      } finally {
        setLoading(false);
      }
    };

    fetchRevelations();
  }, [token]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    if (revelations?.sections) {
      setExpandedSections(new Set(revelations.sections.map(s => s.id)));
    }
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  return (
    <DashboardLayout title="Mes Révélations">
      <div className="max-w-4xl mx-auto">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-transparent flex items-center justify-center mx-auto mb-6">
            <BookOpen className="w-10 h-10 text-[#D4AF37]" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Mes Révélations
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Tout ce que tu as construit et découvert sur toi et ta marque, 
            rassemblé en un seul endroit.
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-16">
            <Loader2 className="w-10 h-10 text-[#D4AF37] mx-auto mb-4 animate-spin" />
            <p className="text-gray-500">Chargement de tes révélations...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-16">
            <p className="text-red-400 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="text-[#D4AF37] hover:underline"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && revelations?.sections?.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <Heart className="w-16 h-16 text-[#D4AF37]/30 mx-auto mb-6" />
            <h2 className="font-serif text-2xl text-white mb-4">
              Ton histoire commence ici
            </h2>
            <p className="text-gray-400 max-w-md mx-auto mb-8">
              Au fur et à mesure de ton parcours, tes révélations apparaîtront ici. 
              Commence par définir ta vision pour voir ta première révélation.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-gold px-8 py-3"
            >
              Commencer mon parcours
            </button>
          </motion.div>
        )}

        {/* Revelations Content */}
        {!loading && !error && revelations?.sections?.length > 0 && (
          <>
            {/* Summary Bar */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8 p-4 bg-[#0A0A0A] border border-[#1C1C1C] rounded-xl"
            >
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-white font-medium">
                    {revelations.summary.total_sections} révélation{revelations.summary.total_sections > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="h-4 w-px bg-[#1C1C1C]" />
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-[#1C1C1C] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#D4AF37] to-[#E5C585]"
                      style={{ width: `${revelations.summary.completion_percentage}%` }}
                    />
                  </div>
                  <span className="text-gray-500 text-sm">
                    {revelations.summary.completion_percentage}%
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={expandAll}
                  className="px-3 py-1.5 text-xs text-gray-400 hover:text-white transition-colors"
                >
                  Tout ouvrir
                </button>
                <button
                  onClick={collapseAll}
                  className="px-3 py-1.5 text-xs text-gray-400 hover:text-white transition-colors"
                >
                  Tout fermer
                </button>
              </div>
            </motion.div>

            {/* Sections */}
            <div className="space-y-4">
              {revelations.sections.map((section, index) => (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <RevelationSection
                    section={section}
                    isExpanded={expandedSections.has(section.id)}
                    onToggle={() => toggleSection(section.id)}
                  />
                </motion.div>
              ))}
            </div>

            {/* Quote Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-12 p-8 bg-gradient-to-br from-[#D4AF37]/5 to-transparent border border-[#D4AF37]/20 rounded-2xl text-center"
            >
              <Quote className="w-8 h-8 text-[#D4AF37]/30 mx-auto mb-4" />
              <p className="font-serif text-xl text-white italic mb-4">
                "Chaque révélation est une étape vers la version la plus alignée de ta marque."
              </p>
              <p className="text-[#D4AF37] text-sm">— Vision Lab</p>
            </motion.div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MyRevelations;
